<template>
  <div class="profile-wrapper profile-centered-wrapper">
    <div class="profile-content">
      <div class="profile-step-back">
        <v-icon color="#333333" size="22" @click="handleStepBack()">
          mdi-arrow-left
        </v-icon>
      </div>

      <h3 class="profile-title">
        {{ $t("profile.spouseData.title") }}
      </h3>

      <v-form ref="form" v-model="valid" class="profile-form">
        <div class="spouse-fields">
          <TextField
            id="spouse-name"
            :label="$t('profile.spouseData.fields.spouseName.label')"
            :value="spouseName"
            :placeholder="$t('profile.spouseData.fields.spouseName.placeholder')"
            :rules="mustCompleteRule"
            @updateValue="spouseName = $event"
            class="profile-input-field"
          />

          <TextField
            id="spouse-family-name"
            :value="spouseFamilyName"
            :label="$t('profile.spouseData.fields.spouseFamilyName.label')"
            :placeholder="$t('profile.spouseData.fields.spouseFamilyName.placeholder')"
            :rules="mustCompleteRule"
            @updateValue="spouseFamilyName = $event"
            class="profile-input-field"
          />

          <TextField
            id="spouse-rut"
            :value="spouseRut"
            :label="$t('profile.spouseData.fields.spouseRut.label')"
            :placeholder="$t('profile.spouseData.fields.spouseRut.placeholder')"
            :rules="rutRules"
            @updateValue="spouseRut = $event"
            class="profile-input-field"
          />

          <AutoComplete
            id="conjugal-regime"
            :value="conjugalRegime"
            :label="$t('profile.spouseData.fields.conjugalRegime.label')"
            :placeholder="$t('profile.spouseData.fields.conjugalRegime.placeholder')"
            :items="displayableConjugalRegimeOptions"
            item-text="label"
            item-value="code"
            @updateValue="conjugalRegime = $event"
            class="profile-input-field"
          />
        </div>

        <NewButton
          @on-click="createCustomerAndSpousePatch()"
          :class="{ 'success-state-btn': successState }"
          :text="$t('signup.shared.buttons.next')"
          :loading="loading"
          :disabled="!valid || loading"
          class="submit-button"
        />

        <ReusableDialog
          id="failed-patch-dialog"
          :value="errorDialog"
          :title="$t('profile.spouseData.errorDialog.title')"
          :text="$t('profile.spouseData.errorDialog.text')"
          @display="errorDialog = $event"
        />

        <HorizontalButtonDialog
          :value="unsavedChangesDialog"
          :title="$t('profile.shared.unsavedChangesDialog.title')"
          :text="$t('profile.shared.unsavedChangesDialog.text')"
          :primaryButtonText="$t('profile.shared.unsavedChangesDialog.buttons.continue')"
          :secondaryButtonText="$t('profile.shared.unsavedChangesDialog.buttons.back')"
          @secondaryClick="unsavedChangesDialog = false"
          @primaryClick="$router.push({ name: 'account' })"
          @display="unsavedChangesDialog = $event"
        />

        <div class="cancel" @click="$router.push({ name: 'account' })">
          {{ $t("base.cancel") }}
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AutoComplete from "@/components/shared/AutoComplete.vue";
import TextField from "@/components/shared/TextField.vue";
import userSelects from "@/mixins/userSelects.js";
import { customersApiFunctions } from "@/mixins/customersApiMixin.js";
import NewButton from "@/components/shared/NewButton.vue";
import ReusableDialog from "@/components/shared/ReusableDialog.vue";
import HorizontalButtonDialog from "@/components/shared/HorizontalButtonDialog.vue";

const { validate, format } = require("rut.js");

export default {
  name: "SpouseData",

  mixins: [userSelects, customersApiFunctions],

  components: {
    NewButton,
    AutoComplete,
    TextField,
    ReusableDialog,
    HorizontalButtonDialog,
  },

  data() {
    return {
      valid: false,
      loading: false,
      spouseName: null,
      spouseFamilyName: null,
      spouseRut: null,
      conjugalRegime: null,
      mustCompleteRule: [v => !!v || this.$t("profile.spouseData.errors.mustComplete")],
      rutRules: [
        v => !!v || this.$t("profile.spouseData.errors.mustComplete"),
        v => validate(v) || this.$t("profile.spouseData.errors.rut"),
      ],
      unsavedChangesDialog: false,
      // Mutation states
      performingMutation: false,
      errorDialog: false,
      successState: false,
    };
  },

  props: ["customerPatchData"],

  computed: {
    ...mapGetters(["profile", "loadingProfile", "customerSpouse"]),

    unchangedForm() {
      return [this.spouseFamilyName, this.spouseRut, this.spouseName].every(
        value => !value,
      );
    },

    displayableConjugalRegimeOptions() {
      if (!this.customerPatchData) return [];
      const maritalStatus = this.customerPatchData?.maritalStatus;
      return this.conjugalRegimeOptions[maritalStatus].map(conjugalRegimeOption => ({
        ...conjugalRegimeOption,
        label: this.$t(
          `profile.spouseData.fields.conjugalRegime.options.${conjugalRegimeOption.code}`,
        ),
      }));
    },
  },

  methods: {
    ...mapActions(["setAccountData"]),

    buildCustomerPatchData() {
      /** conjugal regime is a customer input value
       *  therefore we need to add this attribute to the original customer patch data
       */

      const { conjugalRegime, customerPatchData } = this;
      return {
        payload: {
          ...customerPatchData,
          conjugalRegime,
        },
      };
    },

    buildSpouseData() {
      const { spouseName, spouseFamilyName, spouseRut } = this;
      return {
        identifier: format(spouseRut),
        name: spouseName,
        lastName: spouseFamilyName,
      };
    },

    handleMutationErrors() {
      this.errorDialog = true;
      this.performingMutation = false;
    },

    async patchCustomer() {
      const customerPatchData = this.buildCustomerPatchData();
      const customerPatchResponse = await this.patchCustomerData(customerPatchData);
      return customerPatchResponse;
    },

    async handleSuccessfulMutation(payload) {
      this.setAccountData(payload);
      await this.showSuccessState();
      this.$router.push({ name: "account" });
    },

    async createCustomerAndSpousePatch() {
      try {
        this.performingMutation = true;

        const customerPatchResponse = await this.patchCustomer();
        if (customerPatchResponse.errors) {
          this.handleMutationErrors();
          return;
        }

        const spouseResponse = await this.patchOrCreateSpouse();
        this.performingMutation = false;

        if (spouseResponse.errors) {
          this.handleMutationErrors();
          return;
        }
        this.handleSuccessfulMutation(this.customerPatchData);
      } catch (error) {
        console.error(error);
        this.handleMutationErrors();
      }
    },

    async patchOrCreateSpouse() {
      const spouseData = this.buildSpouseData();
      const spouseId = this.customerSpouse?.id;
      const spouseResponse = spouseId ?
        await this.patchSpouse({ id: Number(spouseId), ...spouseData }) :
        await this.createSpouse(spouseData);

      return spouseResponse;
    },

    async showSuccessState() {
      this.successState = true;
      await new Promise(resolve => setTimeout(resolve, 2500));
      this.successState = false;
    },

    loadConjugalRegime() {
      // If the user reloads the page, the customer patch data is cleared from the web app
      // Therefore, is necessary to recover this data in the personal form
      if (!this.customerPatchData) {
        this.$router.push({ name: "accountPersonal" });
        return;
      }

      const { maritalStatus } = this.customerPatchData;

      this.conjugalRegime = this.conjugalRegimeOptions[maritalStatus].find(
        conjugalRegimeOption => conjugalRegimeOption.default,
      ).code;
    },

    handleStepBack() {
      // If there is no changes, go back directly
      if (this.unchangedForm) {
        this.$router.push({ name: "accountPersonal" });
      } else {
        // Otherwise. display the unsavedChangesDialog
        this.unsavedChangesDialog = true;
      }
    },
  },

  mounted() {
    this.loadConjugalRegime();
  },
};
</script>

<style lang="scss" scoped>
.spouse-fields > :not(:last-child) {
  margin-bottom: 10px;
}

.success-state-btn {
  background-color: #6d48ff !important;
  border: unset;
}

.cancel {
  text-align: right;
  color: #646363;
  font-weight: 500;
  font-size: 14px;
  line-height: 160%;

  margin-top: 40px;
  &:hover {
    cursor: pointer;
  }
}
</style>
